import React, { useState, useEffect } from 'react';
import { Image, Eye, MousePointer, TrendingUp, DollarSign, Users, Target } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { getDateRange } from '../lib/google-ads-client';

interface DisplayCampaign {
  id: string;
  name: string;
  status: string;
  impressions: number;
  clicks: number;
  ctr: number;
  costMicros: number;
  conversions: number;
  viewThroughConversions: number;
  reach: number;
  frequency: number;
}

export default function DisplayCampaignManagerPage() {
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState<DisplayCampaign[]>([]);
  const [totalMetrics, setTotalMetrics] = useState({
    impressions: 0,
    clicks: 0,
    cost: 0,
    conversions: 0,
    reach: 0
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const range = getDateRange(30);
      const { data } = await supabase
        .from('google_ads_campaign_performance')
        .select('*')
        .eq('campaign_type', 'DISPLAY')
        .gte('date', range.startDate)
        .lte('date', range.endDate);

      if (data) {
        const displayCampaigns = aggregateCampaigns(data);
        setCampaigns(displayCampaigns);
        calculateTotals(displayCampaigns);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const aggregateCampaigns = (data: any[]): DisplayCampaign[] => {
    const grouped = new Map<string, any[]>();
    
    data.forEach(row => {
      if (!grouped.has(row.campaign_id)) {
        grouped.set(row.campaign_id, []);
      }
      grouped.get(row.campaign_id)?.push(row);
    });

    return Array.from(grouped.entries()).map(([id, rows]) => {
      const totals = rows.reduce((acc, r) => ({
        impressions: acc.impressions + (r.impressions || 0),
        clicks: acc.clicks + (r.clicks || 0),
        costMicros: acc.costMicros + (r.cost_micros || 0),
        conversions: acc.conversions + (r.conversions || 0),
        viewThroughConversions: acc.viewThroughConversions + (r.view_through_conversions || 0)
      }), { impressions: 0, clicks: 0, costMicros: 0, conversions: 0, viewThroughConversions: 0 });

      const ctr = totals.impressions > 0 ? (totals.clicks / totals.impressions) * 100 : 0;

      return {
        id,
        name: rows[0]?.campaign_name || 'Display Campaign',
        status: rows[0]?.campaign_status || 'ENABLED',
        impressions: totals.impressions,
        clicks: totals.clicks,
        ctr,
        costMicros: totals.costMicros,
        conversions: totals.conversions,
        viewThroughConversions: totals.viewThroughConversions,
        reach: totals.impressions, // Approximation
        frequency: totals.impressions > 0 ? totals.impressions / (totals.impressions * 0.7) : 0
      };
    });
  };

  const calculateTotals = (campaigns: DisplayCampaign[]) => {
    const totals = campaigns.reduce((acc, c) => ({
      impressions: acc.impressions + c.impressions,
      clicks: acc.clicks + c.clicks,
      cost: acc.cost + (c.costMicros / 1000000),
      conversions: acc.conversions + c.conversions,
      reach: acc.reach + c.reach
    }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, reach: 0 });

    setTotalMetrics(totals);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Display Campaign Manager</h1>
        <p className="text-gray-600 mt-1">Creative performance tracking and audience analysis</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Impressions</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{totalMetrics.impressions.toLocaleString()}</p>
            </div>
            <Eye className="w-8 h-8 text-blue-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Clicks</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{totalMetrics.clicks.toLocaleString()}</p>
            </div>
            <MousePointer className="w-8 h-8 text-purple-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Cost</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">${totalMetrics.cost.toFixed(2)}</p>
            </div>
            <DollarSign className="w-8 h-8 text-green-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Reach</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{totalMetrics.reach.toLocaleString()}</p>
            </div>
            <Users className="w-8 h-8 text-orange-600" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold">Display Campaign Performance</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campaign</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Impressions</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Clicks</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">CTR</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cost</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Conversions</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">View-Through</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {campaigns.map((campaign) => (
                <tr key={campaign.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Image className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{campaign.name}</div>
                        <div className="text-xs text-gray-500">{campaign.status}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{campaign.impressions.toLocaleString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{campaign.clicks.toLocaleString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{campaign.ctr.toFixed(2)}%</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">${(campaign.costMicros / 1000000).toFixed(2)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{campaign.conversions}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{campaign.viewThroughConversions}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
